import React from 'react';
import { motion } from 'framer-motion';
import { FaBriefcase } from 'react-icons/fa';
import { useExperiences } from '../hooks/useFirestoreData';
import '../styles/Experience.css';

/*
  Firestore collection: experiences
  Each document:
  {
    order: 1,
    company: "NexGen Technologies",
    role: "Senior Software Engineer",
    period: "Jan 2023 — Present",
    achievements: ["...", "...", "..."]
  }
*/

const DEFAULT_EXPERIENCES = [
  {
    company: 'ServiceNow',
    location: 'Hyderabad, India',
    role: 'Staff Software Engineer',
    period: 'Feb 2022 — Present',
    achievements: [
      'Leading architecture and development of enterprise-scale UI platforms serving millions of users globally.',
      'Driving adoption of modern frontend frameworks and design systems across multiple product lines.',
      'Mentoring engineering teams and establishing best practices for performance, accessibility, and code quality.',
    ],
  },
  {
    company: 'TA Digital (now known as Credera)',
    location: 'Hyderabad, India',
    role: 'Associate Architect | UI & Mobility',
    period: 'Mar 2019 — Feb 2022',
    achievements: [
      'Architected and delivered large-scale responsive web applications for Fortune 500 clients using React and Angular.',
      'Led UI/UX strategy and mobile-first design initiatives, improving user engagement metrics by 35%.',
      'Managed cross-functional teams and coordinated with stakeholders to align technical solutions with business goals.',
    ],
  },
  {
    company: 'Infogain India Pvt. Ltd.',
    location: 'Noida, India',
    role: 'Technology Lead | UI/UX',
    period: 'May 2016 — Mar 2019',
    achievements: [
      'Spearheaded frontend architecture for multiple client projects, establishing reusable component libraries and coding standards.',
      'Conducted technical evaluations and POCs for emerging UI technologies, driving innovation within the team.',
      'Collaborated with UX designers to translate wireframes and prototypes into pixel-perfect, accessible interfaces.',
    ],
  },
  {
    company: 'Sapient Consulting Pvt. Ltd.',
    location: 'Gurgaon, India',
    role: 'Senior Associate L2 | Experience Technology',
    period: 'Jan 2014 — May 2016',
    achievements: [
      'Developed interactive, high-performance web experiences for global brands using JavaScript, HTML5, and CSS3.',
      'Contributed to the experience technology practice by building internal tools and shared UI frameworks.',
      'Worked in agile squads delivering end-to-end frontend solutions from concept through production deployment.',
    ],
  },
  {
    company: 'Times Internet Ltd.',
    location: 'Delhi, India',
    role: 'Sr. Officer | UI Development',
    period: 'Apr 2012 — Oct 2013',
    achievements: [
      'Built and maintained responsive frontends for high-traffic media properties serving millions of daily visitors.',
      'Optimized page load performance and cross-browser compatibility across desktop and mobile platforms.',
      'Partnered with editorial and product teams to rapidly iterate on user-facing features and layouts.',
    ],
  },
  {
    company: 'WebCatalyst',
    location: 'Noida, India',
    role: 'UI Developer',
    period: 'May 2008 — Apr 2012',
    achievements: [
      'Developed semantic, standards-compliant websites and landing pages for diverse client portfolios.',
      'Built expertise in cross-browser CSS, JavaScript DOM manipulation, and progressive enhancement techniques.',
      'Contributed to the full project lifecycle from requirements gathering through deployment and maintenance.',
    ],
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.7, ease: [0.25, 0.8, 0.25, 1] },
  }),
};

function Experience() {
  const { data: experiences } = useExperiences(DEFAULT_EXPERIENCES);

  return (
    <section className="experience page-wrapper">
      <div className="experience-container">
        <motion.h1
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Experience
        </motion.h1>

        <div className="timeline">
          <div className="timeline-line" />

          {experiences.map((exp, index) => (
            <motion.div
              className="timeline-item"
              key={exp.id || index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={fadeInUp}
              custom={index}
            >
              <div className="timeline-node">
                <div className="node-ring">
                  <FaBriefcase />
                </div>
                <div className="node-pulse" />
              </div>

              <div className="glass-card timeline-card">
                <div className="card-header">
                  <h3 className="exp-role">{exp.role}</h3>
                  <span className="exp-period">{exp.period}</span>
                </div>
                <h4 className="exp-company">
                  {exp.company}
                  {exp.location && <span className="exp-location"> — {exp.location}</span>}
                </h4>
                <ul className="exp-achievements">
                  {exp.achievements.map((item, i) => (
                    <li key={i}>
                      <span className="achievement-bullet" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Experience;
